import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Switch } from '@/components/ui/Switch'
import { ReservationSettingConditionList } from '@/components/venue/ReservationSettingConditionList'
import { ReservationSettingDisableConfirmModal } from '@/components/venue/ReservationSettingDisableConfirmModal'
import { ReservationSettingHistoryTable } from '@/components/venue/ReservationSettingHistoryTable'
import { useUpdateVenueReservationSetting } from '@/hooks/useUpdateVenueReservationSetting'
import { useVenueReservationSetting, venueReservationSettingQueryKey } from '@/hooks/useVenueReservationSetting'
import { useVenueReservationSettingHistories } from '@/hooks/useVenueReservationSettingHistories'
import { ApiError, toApiError } from '@/lib/api/errors'
import {
  isReservationSwitchDisabled,
  RESERVATION_FEE_FREE_NOTICE,
  RESERVATION_FEE_SETTLEMENT_FACTS,
  RESERVATION_FOOTER_NOTICE,
  RESERVATION_UNMET_BANNER_TEXT,
  reservationStatusSubtitle,
} from '@/lib/venue/reservationSettingViewModel'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 예약 기능 설정 — 'reservation-setting'(설계 §2, 요구사항 #758).
 *
 * 🚨 이 화면은 조립만 한다. 표시값 파생은 `reservationSettingViewModel.ts`, 조건별 이동 경로는
 * `ReservationSettingConditionList`가 소유한다(설계 D).
 */
export function VenueReservationSettingPage() {
  const { venueId } = useVenueContext()
  const queryClient = useQueryClient()

  const settingQuery = useVenueReservationSetting(venueId)
  const historiesQuery = useVenueReservationSettingHistories(venueId)
  const updateMutation = useUpdateVenueReservationSetting(venueId)

  const [showDisableModal, setShowDisableModal] = useState(false)
  const [switchError, setSwitchError] = useState<ApiError | null>(null)

  const view = settingQuery.data

  function handleToggle(nextChecked: boolean) {
    if (!nextChecked) {
      setShowDisableModal(true)
      return
    }

    setSwitchError(null)
    updateMutation.mutate(
      { enabled: true },
      {
        onError: (error) => {
          const apiError = toApiError(error)
          setSwitchError(apiError)
          // 🚨 클릭 시점과 조회 시점 사이 조건이 무너진 레이스일 수 있다 — 체크리스트를 최신화한다.
          if (apiError.isReservationActivationBlocked) {
            void queryClient.invalidateQueries({ queryKey: venueReservationSettingQueryKey(venueId) })
          }
        },
      }
    )
  }

  const historyPages = historiesQuery.data?.pages ?? []
  const historyItems = historyPages.flatMap((page) => page.histories)

  return (
    <>
      <PageHeader title="예약 기능 설정" description="구장의 온라인 예약 기능을 켜고 끕니다." />

      {settingQuery.isLoading ? <LoadingState rows={4} label="예약 기능 설정을 불러오는 중" /> : null}

      {settingQuery.isError ? (
        <ErrorState
          error={settingQuery.error}
          onRetry={() => {
            void settingQuery.refetch()
          }}
        />
      ) : null}

      {view ? (
        <div className="space-y-5">
          <Card>
            <CardBody className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-ink-700">온라인 예약 기능</p>
                <p className="mt-0.5 text-sm text-slateish-600">{reservationStatusSubtitle(view)}</p>
              </div>
              <Switch
                checked={view.reservationEnabled}
                onChange={handleToggle}
                disabled={isReservationSwitchDisabled(view) || updateMutation.isPending}
                aria-label="온라인 예약 기능"
              />
            </CardBody>
          </Card>

          {switchError ? (
            <Alert tone="danger">
              <p>{switchError.message}</p>
              {switchError.reservationUnmetConditions ? (
                <ul className="mt-1 list-disc pl-4">
                  {switchError.reservationUnmetConditions.map((condition) => (
                    <li key={condition.condition}>{condition.label}</li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}

          {!view.canEnable ? <Alert tone="warning">{RESERVATION_UNMET_BANNER_TEXT}</Alert> : null}

          <Card>
            <CardHeader title="활성화 조건" />
            <CardBody>
              <ReservationSettingConditionList venueId={venueId} conditions={view.conditions} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="수수료·정산 안내" />
            <CardBody className="space-y-4">
              <Alert tone="info">{RESERVATION_FEE_FREE_NOTICE}</Alert>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {RESERVATION_FEE_SETTLEMENT_FACTS.map((fact) => (
                  <div key={fact.label}>
                    <dt className="text-xs text-slateish-500">{fact.label}</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </CardBody>
          </Card>

          <Alert tone="info">{RESERVATION_FOOTER_NOTICE}</Alert>

          <Card>
            <CardHeader title="변경 이력" description="최신순으로 조회됩니다." />
            <CardBody>
              {historiesQuery.isLoading ? <LoadingState rows={3} label="변경 이력을 불러오는 중" /> : null}

              {historiesQuery.isError ? (
                <ErrorState
                  error={historiesQuery.error}
                  onRetry={() => {
                    void historiesQuery.refetch()
                  }}
                />
              ) : null}

              {!historiesQuery.isLoading && !historiesQuery.isError ? (
                <ReservationSettingHistoryTable
                  items={historyItems}
                  hasNextPage={historiesQuery.hasNextPage ?? false}
                  isFetchingNextPage={historiesQuery.isFetchingNextPage}
                  onLoadMore={() => {
                    void historiesQuery.fetchNextPage()
                  }}
                />
              ) : null}
            </CardBody>
          </Card>
        </div>
      ) : null}

      {showDisableModal ? (
        <ReservationSettingDisableConfirmModal
          venueId={venueId}
          onClose={() => {
            setShowDisableModal(false)
          }}
          onDisabled={() => {
            setShowDisableModal(false)
          }}
        />
      ) : null}
    </>
  )
}
